// ============================================================================
// SCHOOL — the derived half.
//
// docs/design/handoff/README.md §State Management lists these under "Derived, never stored":
// time-remaining strings and their colour thresholds, the runway's day headers, scope and count
// lines. Everything in this file is a pure function of data the API already returned plus the
// clock the caller was handed, so nothing here can drift out of step with the rows it describes
// and nothing reads the wall clock (demo mode's clock is fixed; a component that cheats makes
// every future screenshot non-reproducible).
// ============================================================================

import {
  addDays,
  courseSlug,
  datedAssignments,
  daysBetween,
  formatShortDate,
  minutesToClock,
  relativeDay,
  weekdayName,
  weekdayOf,
  type Assignment,
  type Course,
  type DatedAssignment,
  type Lecture,
  type MeetingKind,
  type Term,
} from '@app/shared'

import {
  clockFromTimestamp,
  localDayOf,
  localMinutesOfDay,
  timeRemaining,
  type TimeTone,
} from '../../lib/time'

export { courseSlug }

// ── Course hues ─────────────────────────────────────────────────────────────

/**
 * The four courses the handoff itself named (README §3 and the 2×2 grid in 6a) keep their exact
 * colours: COGSCI 175 school-blue, NEU C61 learning-teal, LINGUIS C142 interests-violet, COGSCI 1
 * tasks-orange. This is a lookup, not a hash, for these four specifically — the owner has already
 * learned them over a semester, and a resort on every new course would relearn all four for
 * nothing.
 */
const COURSE_HUES: Record<string, string> = {
  'COGSCI 175': 'school',
  'NEU C61': 'learning',
  'LINGUIS C142': 'interests',
  'COGSCI 1': 'tasks',
}

/**
 * The two hues the table above does not claim, held in reserve for whatever course Canvas adds
 * that the handoff never named.
 *
 * BUG THIS REPLACES (owner screenshot, 2026-08-29): every unlisted course fell back to the
 * School hue, so a real five-course semester rendered COGSCI 175, COGSCI 180 and COGSCI C142 as
 * the same blue bar. Falling back to a SECOND fixed hue would only have moved the collision to
 * three courses instead of two, so unlisted courses now get assigned out of these two spares by
 * where their code sorts among every OTHER unlisted code — deterministic and stable across a
 * reload regardless of which order the API happens to return the roster in, and two spares is
 * exactly the head-room a real Berkeley course load has ever needed here (never more than two
 * courses outside the four the handoff drew).
 */
const OVERFLOW_HUES = ['chat', 'calendar'] as const

/** Every unlisted code the current roster carries, sorted — set once per `registerCourseCodes` call. */
let overflowCodes: readonly string[] = []

/**
 * Call this with every course code the roster currently has, the moment it is known (School.tsx
 * and MobileSchool.tsx both do, right after `useCourses()` resolves). Idempotent and safe to call
 * from more than one screen: it always recomputes the full sorted overflow list from scratch, so
 * whichever screen loads the roster first, the result is the same.
 */
export function registerCourseCodes(codes: readonly string[]): void {
  overflowCodes = [...new Set(codes.filter((c) => !(c in COURSE_HUES)))].sort()
}

export function courseHue(code: string): string {
  const named = COURSE_HUES[code]
  if (named) return named
  const known = overflowCodes.indexOf(code)
  if (known !== -1) return OVERFLOW_HUES[known % OVERFLOW_HUES.length]!
  // Not registered yet — a course page opened by a direct link before the overview has ever
  // loaded this session. Sort it into the position it will land in once the roster IS known, so
  // this guess and the registered answer agree the moment registerCourseCodes catches up.
  const guess = [...overflowCodes, code].sort().indexOf(code)
  return OVERFLOW_HUES[guess % OVERFLOW_HUES.length]!
}

/** Tailwind class strings, written out in full because the scanner cannot read a template. */
const HUE_BG: Record<string, string> = {
  school: 'bg-school',
  learning: 'bg-learning',
  interests: 'bg-interests',
  tasks: 'bg-tasks',
  chat: 'bg-chat',
  calendar: 'bg-calendar',
}
const HUE_BORDER: Record<string, string> = {
  school: 'border-school',
  learning: 'border-learning',
  interests: 'border-interests',
  tasks: 'border-tasks',
  chat: 'border-chat',
  calendar: 'border-calendar',
}
const HUE_UNREAD: Record<string, string> = {
  school: 'bg-info-fill text-info',
  learning: 'bg-teal-fill text-teal',
  interests: 'bg-accent-fill text-accent',
  tasks: 'bg-orange-fill-2 text-orange',
  // No dedicated fill token exists for these two (only the four the handoff drew got one) — a
  // tinted version of the domain hue itself, same idiom, rather than inventing a new token pair.
  chat: 'bg-chat/10 text-chat',
  calendar: 'bg-calendar/15 text-calendar',
}

export const hueBg = (code: string) => HUE_BG[courseHue(code)] ?? HUE_BG.school!
export const hueBorder = (code: string) => HUE_BORDER[courseHue(code)] ?? HUE_BORDER.school!
export const hueUnreadPill = (code: string) => HUE_UNREAD[courseHue(code)] ?? HUE_UNREAD.school!

/** The same per-course hue, as a `var(--color-…)` reference for `--mo-rule` (motion-brief §2's
 *  straight-rule mechanic, which takes its colour from an inline custom property rather than a
 *  Tailwind class). */
const HUE_VAR: Record<string, string> = {
  school: 'var(--color-school)',
  learning: 'var(--color-learning)',
  interests: 'var(--color-interests)',
  tasks: 'var(--color-tasks)',
  chat: 'var(--color-chat)',
  calendar: 'var(--color-calendar)',
}
export const hueVar = (code: string) => HUE_VAR[courseHue(code)] ?? HUE_VAR.school!

// ── The next class ──────────────────────────────────────────────────────────

const KIND_ABBREV: Record<MeetingKind, string> = {
  lecture: 'LEC',
  discussion: 'DIS',
  lab: 'LAB',
}

export interface NextMeeting {
  kind: MeetingKind
  date: string
  startMinutes: number
  location: string
}

/**
 * The next time this course meets, looking forward at most a fortnight.
 *
 * Computed from the course's own meeting pattern rather than fetched, because a fetch would be
 * one request per course per render for something that is pure arithmetic. Holidays and the end
 * of instruction are honoured: a course that has finished for the term returns null, and the
 * card says so instead of naming a class that will not happen.
 */
export function nextMeeting(
  course: Course,
  term: Term | null | undefined,
  today: string,
  nowMinutes: number,
): NextMeeting | null {
  if (!term) return null
  for (let offset = 0; offset <= 14; offset++) {
    const date = addDays(today, offset)
    if (date > term.instructionEnds) return null
    if (date < term.instructionStarts) continue
    if (term.holidays.includes(date)) continue
    const wd = weekdayOf(date)
    const onThisDay = course.meetings
      .filter((m) => m.weekday === wd)
      .filter((m) => offset > 0 || m.endMinutes > nowMinutes)
      .sort((a, b) => a.startMinutes - b.startMinutes)
    const next = onThisDay[0]
    if (next) {
      return {
        kind: next.kind,
        date,
        startMinutes: next.startMinutes,
        location: next.location,
      }
    }
  }
  return null
}

/** 'LEC · tomorrow 2:00 PM · Wheeler 212'. */
export function nextMeetingPhrase(meeting: NextMeeting, today: string): string {
  return `${KIND_ABBREV[meeting.kind]} · ${relativeDay(meeting.date, today)} ${minutesToClock(
    meeting.startMinutes,
  )} · ${meeting.location}`
}

// ── The next deadline ───────────────────────────────────────────────────────

/**
 * 'Reading response 2 · 14h overdue' · 'Problem set 1 · today 11:59pm'.
 *
 * The title is cut at its colon because a course card has roughly 200px for this line and the
 * subtitle of an assignment ("…: The Extended Mind") is the half you can lose. The whole title
 * is on the course page, one click away, and is never truncated there.
 */
export function nextDeadlinePhrase(
  assignment: DatedAssignment,
  today: string,
  nowMinutes: number,
): { text: string; tone: TimeTone } {
  const short = assignment.title.split(':')[0]!.trim()
  const t = timeRemaining(assignment.dueAt, today, nowMinutes)
  if (t.tone === 'overdue') {
    // The label already carries a minus sign for the runway's tight column; spelled out here.
    return { text: `${short} · ${t.label.replace('−', '')} overdue`, tone: t.tone }
  }
  const day = relativeDay(localDayOf(assignment.dueAt), today)
  return { text: `${short} · ${day} ${clockFromTimestamp(assignment.dueAt)}`, tone: t.tone }
}

// ── Deadlines: the runway ───────────────────────────────────────────────────

export interface RunwayGroup {
  key: string
  /** 'OVERDUE' · 'TODAY · WED 16' · 'FRI 18'. */
  label: string
  overdue: boolean
  items: DatedAssignment[]
}

/**
 * The merged two-week runway, grouped by day.
 *
 * OVERDUE IS ITS OWN GROUP AND IT IS ALWAYS FIRST (README §3: "Overdue items are always pinned
 * and never softened"). It is not sorted in with the rest by date, because a deadline three days
 * gone would otherwise sink below tomorrow's and stop being a problem on screen while remaining
 * one in life.
 */
export function runwayGroups(
  assignments: readonly Assignment[],
  today: string,
  nowMinutes: number,
  days = 14,
): RunwayGroup[] {
  const horizon = addDays(today, days)
  const overdue: DatedAssignment[] = []
  const byDay = new Map<string, DatedAssignment[]>()

  // UNDATED WORK IS NOT ON A TIMELINE. Most Canvas assignments carry no due date at all, and a
  // runway cannot place one — it is not dropped from the app, only from this view, and the
  // course's own table shows it with the reason. See datedAssignments() in @app/shared.
  for (const a of openRunwayWork(assignments)) {
    const tone = timeRemaining(a.dueAt, today, nowMinutes).tone
    if (tone === 'overdue') {
      overdue.push(a)
      continue
    }
    const day = a.dueAt.slice(0, 10)
    if (day > horizon) continue
    const bucket = byDay.get(day) ?? []
    bucket.push(a)
    byDay.set(day, bucket)
  }

  const groups: RunwayGroup[] = []
  if (overdue.length > 0) {
    groups.push({
      key: 'overdue',
      label: 'OVERDUE',
      overdue: true,
      items: overdue.sort((a, b) => (a.dueAt < b.dueAt ? -1 : 1)),
    })
  }
  for (const day of [...byDay.keys()].sort()) {
    groups.push({
      key: day,
      label: runwayDayLabel(day, today),
      overdue: false,
      items: byDay.get(day)!.sort((a, b) => (a.dueAt < b.dueAt ? -1 : 1)),
    })
  }
  return groups
}

/**
 * The work a runway has any business placing: dated, and not yet handed in.
 *
 * A DONE THING IS NEVER OVERDUE. The runway's tones come from the clock alone, so a submitted or
 * graded assignment whose deadline has passed would land under OVERDUE — which is exactly what
 * the owner saw on his phone (2026-09-18): ten bCourses items he had finished, pinned in red.
 * The desktop asks the API for open work only; this filter makes the runway say the same thing
 * whichever list it is handed. `missing` stays — that is Canvas's own word for overdue.
 */
export function openRunwayWork(assignments: readonly Assignment[]): DatedAssignment[] {
  return datedAssignments(assignments).filter((a) => a.status !== 'submitted' && a.status !== 'graded')
}

/** 'TODAY · WED 16' · 'TOMORROW · THU 17' · 'FRI 18'. */
export function runwayDayLabel(date: string, today: string): string {
  const stamp = `${weekdayName(date, true).toUpperCase()} ${Number(date.slice(8, 10))}`
  const delta = daysBetween(today, date)
  if (delta === 0) return `TODAY · ${stamp}`
  if (delta === 1) return `TOMORROW · ${stamp}`
  return stamp
}

/**
 * '1 overdue · 4 due this week · 4 next week'.
 *
 * "This week" ends on Sunday, so on a Friday it means two days and not seven — which is the
 * point of saying it at all. The counts come out of the same list the rows come out of.
 */
export function runwaySummary(
  assignments: readonly Assignment[],
  today: string,
  nowMinutes: number,
): { overdue: number; thisWeek: number; nextWeek: number } {
  // Sunday closes the week; weekdayOf() is 0 for Sunday.
  const wd = weekdayOf(today)
  const endOfThisWeek = addDays(today, wd === 0 ? 0 : 7 - wd)
  const endOfNextWeek = addDays(endOfThisWeek, 7)

  let overdue = 0
  let thisWeek = 0
  let nextWeek = 0
  // Same rule as the runway: a count of "due this week" cannot include work with no due date.
  for (const a of openRunwayWork(assignments)) {
    if (timeRemaining(a.dueAt, today, nowMinutes).tone === 'overdue') {
      overdue++
      continue
    }
    const day = a.dueAt.slice(0, 10)
    if (day <= endOfThisWeek) thisWeek++
    else if (day <= endOfNextWeek) nextWeek++
  }
  return { overdue, thisWeek, nextWeek }
}

// ── Assignments table ───────────────────────────────────────────────────────

/**
 * 'Aug 17 · 11:59pm', or 'Oct 8 · in class' when the deadline lands inside one of this course's
 * own meetings. "In class" is DERIVED — it is what a 2:00pm exam on a day the course meets at
 * 2:00pm actually means — and it is never stored, so it cannot survive a timetable change.
 */
export function dueCellLabel(assignment: Assignment, course: Course | null | undefined): string {
  // NOT A DASH, AND NOT A BLANK. An assignment Canvas never gave a deadline is a real
  // assignment; the cell says which of the two it is rather than leaving the reader to guess.
  if (!assignment.dueAt) return 'no due date in Canvas'
  const day = localDayOf(assignment.dueAt)
  const minutes = localMinutesOfDay(assignment.dueAt)
  const inClass =
    course?.meetings.some(
      (m) =>
        m.weekday === weekdayOf(day) && minutes >= m.startMinutes && minutes < m.endMinutes,
    ) ?? false
  return `${formatShortDate(day)} · ${inClass ? 'in class' : clockFromTimestamp(assignment.dueAt)}`
}

/**
 * 'reading response' — the enum, spelled the way a person would read it aloud.
 *
 * 'unclassified' renders as an em dash: Canvas has no assignment-type field, so printing the
 * word would put a label in the TYPE column that says nothing while looking like it says
 * something. The dash is the honest cell, and the title attribute on the row explains it.
 */
export function assignmentKindLabel(kind: Assignment['kind']): string {
  return kind === 'unclassified' ? '—' : kind.replace(/_/g, ' ')
}

/**
 * The row's status word. `missing` is rendered 'overdue' because that is what it means to the
 * owner — Canvas's word for it describes the submission, not the deadline.
 */
export function assignmentStatusLabel(a: Assignment): string {
  return a.status === 'missing' ? 'overdue' : a.status
}

// ── The detail peek's lines (2026-08-29) ────────────────────────────────────
//
// One function per row of the peek, so each absence is answered in exactly one place and in
// WORDS. Every one of them returns null when Canvas said nothing, and the panel then omits the
// row — no dash, no "unknown", no invented default. The README's rule for the grade block
// ("hidden ≠ missing ≠ a score"), applied to five smaller facts.

/** 'Sep 27 · 11:59pm' — the same shape as the DUE cell, for the LOCKS row. Null when there is none. */
export function lockLabel(assignment: Assignment): string | null {
  if (!assignment.lockAt) return null
  return `${formatShortDate(localDayOf(assignment.lockAt))} · ${clockFromTimestamp(assignment.lockAt)}`
}

/**
 * 'online upload' · 'online quiz, online text entry'. CANVAS'S OWN WORDS, underscores opened out
 * and nothing else — this is how the work is HANDED IN, and translating 'online_upload' into
 * "essay" is the taxonomy Canvas does not have (types.ts → AssignmentKind).
 *
 * Null for the empty array, which is a real Canvas state ('none' assignments, and helper items).
 */
export function submissionTypesLabel(assignment: Assignment): string | null {
  const types = assignment.submissionTypes.filter((t) => t.trim())
  if (types.length === 0) return null
  return types.map((t) => t.replace(/_/g, ' ')).join(', ')
}

/** 'points' · 'pass fail'. Canvas's own word, underscores opened out. Null when it gave none. */
export function gradingTypeLabel(assignment: Assignment): string | null {
  const raw = assignment.gradingType?.trim()
  return raw ? raw.replace(/_/g, ' ') : null
}

/**
 * '9.5 / 10' when it is marked, '10 points' when it is not, null when Canvas stated no total.
 *
 * NEVER '0 / 10' FOR UNMARKED WORK — that is the AssignmentsTable's em-dash rule, in a sentence
 * instead of a cell. An unmarked assignment has no score; it has a total it will be marked out of.
 */
export function pointsLabel(assignment: Assignment): string | null {
  const total = assignment.pointsPossible
  if (assignment.score !== null) return `${assignment.score} / ${total}`
  if (!total) return null
  return `${total} ${total === 1 ? 'point' : 'points'}`
}

// ── Lectures ────────────────────────────────────────────────────────────────

export const LECTURE_STATUS_LABEL: Record<Lecture['status'], string> = {
  not_recorded: 'not recorded',
  audio_uploaded: 'audio uploaded',
  processing: 'processing on PC',
  transcribed: 'transcribed',
  failed: 'failed · will retry',
}

/**
 * The transcription chips on a course card: '4 lectures transcribed', '1 processing',
 * '1 queued'. A count of zero produces no chip rather than a '0' — a zero chip is a claim
 * dressed as a statistic.
 */
export function lecturePipelineCounts(lectures: readonly Lecture[]) {
  return {
    transcribed: lectures.filter((l) => l.status === 'transcribed').length,
    processing: lectures.filter((l) => l.status === 'processing').length,
    queued: lectures.filter((l) => l.status === 'audio_uploaded').length,
    recorded: lectures.filter((l) => l.status !== 'not_recorded').length,
  }
}

/** '78 min' from seconds. Null duration means no recording, so there is nothing to say. */
export function durationMinutes(durationSec: number | null): string | null {
  return durationSec === null ? null : `${Math.round(durationSec / 60)} min`
}

// ── Sync freshness ──────────────────────────────────────────────────────────

/**
 * '12 min ago' · '3 h ago' · 'yesterday' · 'Sep 12'.
 *
 * The boundary is the CALENDAR DAY, not a rolling 24 hours: something posted at 4pm yesterday is
 * "yesterday" even though it is nineteen hours old, because that is how the owner thinks about
 * it. A rolling window produces "19 h ago" for a thing that happened before they went to bed,
 * which is a number nobody converts back into a day.
 */
export function agoPhrase(timestamp: string, today: string, nowMinutes: number): string {
  const day = timestamp.slice(0, 10)
  const daysAgo = daysBetween(day, today)
  if (daysAgo === 0) {
    const minutes = Number(timestamp.slice(11, 13)) * 60 + Number(timestamp.slice(14, 16))
    const delta = nowMinutes - minutes
    if (delta < 0) return 'just now' // a clock skew is not worth a negative number on screen
    if (delta < 60) return `${delta} min ago`
    return `${Math.floor(delta / 60)} h ago`
  }
  if (daysAgo === 1) return 'yesterday'
  if (daysAgo < 0) return 'just now'
  return formatShortDate(day)
}

/** 'in 22d' for a date inside the term's reach, else the month — the syllabus exam rows. */
export function examCountdown(date: string, today: string): string {
  const days = daysBetween(today, date)
  if (days < 0) return 'past'
  if (days <= 60) return `${days}d`
  // Beyond two months a day count stops being a number anyone acts on. The month is the answer.
  return formatShortDate(date).split(' ')[0]!
}
